/**
 * Раздел «Потребности СНП» страницы «Аналитика» — облегчённая версия
 * (US-04.9.1). Компоновка по дизайну от 09.09.2026: ряд стат-карточек
 * (СНП · население · градации отклонений) → «Темы отклонений» на всю
 * ширину → виджеты-таблицы в две колонки → все они собраны одним
 * набором табличных примитивов (`snpTable`): «подпись · [бар] · доля ·
 * значение» + строки-итоги.
 *
 * Колонки раскладываются балансировщиком (`balanceColumns`), а не
 * фиксированным разбиением: часть блоков выключается супер-админом
 * (FR-6.1), а высота остальных зависит от данных территории — при
 * жёстком разбиении под короткой колонкой оставалось пустое место.
 *
 * Источник данных — гибрид (как остальные секции «Аналитики»): демо-КАТО →
 * мок hardData/analyticsSnpNeeds.json (Excel «Мониторинг СНП», 2024);
 * остальные КАТО — запрос /api/public/analytics/snp-needs (агрегация до
 * района/области — на бэке). Виджет без данных остаётся с заголовком и
 * подписью «нет данных» (замечание ревью от 11.08.2026), без ошибки
 * интерфейса.
 *
 * Показатели приходят парами {present, answered}: знаменатель у каждого
 * СВОЙ — сколько сёл ответило именно по этому показателю (графы
 * «Мониторинга» заполнены неравномерно: автобусное сообщение — 45 сёл из
 * 585, медпункт — 512). Поэтому доли считаются от `answered`, а «N из M»
 * печатается там, где знаменатель отличается от числа сёл территории
 * (`CountCell`); «нет связи / нет автобуса» — обратная пара (`without`).
 * Распределение сёл по градациям берётся готовым из ответа
 * (deviation_distribution), список сёл — уже отсортированным.
 *
 * Видимость блоков — модули супер-админа (FR-6.1, useModulePresent,
 * default-visible). `summary` — режим витрины «Все» (US-04.1 FR-2.2,
 * виджеты №14–16): «Водоснабжение», «Образование», «Здравоохранение»
 * (покрытие сёл, без кадров); «Экологическое благополучие» (№17) —
 * сектор полной US-04.9, в облегчённой версии данных нет.
 */
import { ClipboardList } from 'lucide-react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { SnpThemes } from './SnpThemes';
import { SnpVillages } from './SnpVillages';
import { SNP_GRADES, gradeOf } from './snpGrades';
import {
  BarCell,
  CountCell,
  FootRow,
  HeadCell,
  NoData,
  ROW,
  ShareCell,
  TextCell,
  ValueCell,
  WidgetCard,
  fmt,
  fmt1,
  sharePct,
  useCountLabel,
} from './snpTable';
import { BlockLoader } from '../../../components/common/BlockLoader';
import {
  Table,
  TableBody,
  TableHeader,
  TableRow,
} from '../../../components/ui/table';
import { StatCard } from '../../../components/widgets/StatCard';
import analyticsSnpNeeds from '../../../hardData/analyticsSnpNeeds.json';
import { useModulePresent } from '../../../hooks/useModulePresent';
import { getAnalyticsSnpNeeds } from '../../../requests/analytics.request';
import { useAnalyticsSection } from '../useAnalyticsSection';
import type {
  SnpComms,
  SnpCount,
  SnpEducation,
  SnpElectricity,
  SnpGas,
  SnpHealthcare,
  SnpNeedsData,
  SnpTheme,
  SnpWater,
} from '../../../types/snpNeeds';

const EMPTY: SnpNeedsData = {
  year: null,
  totalVillages: null,
  population: null,
  themes: [],
  villages: [],
  distribution: null,
  engineering: { water: null, electricity: null, gas: null, comms: null },
  roads: null,
  education: null,
  healthcare: null,
};

/**
 * «Обратная» пара к охвату: сёла, которые ответили, но под условие не
 * попали (нет сотовой связи, нет автобуса). Знаменатель сохраняется.
 */
const without = (count: SnpCount | null | undefined): SnpCount | null => {
  if (count == null) return null;
  const missing =
    count.present !== null && count.answered !== null
      ? count.answered - count.present
      : null;
  return { present: missing, answered: count.answered };
};

/**
 * Обеспеченность местами: вместимость к потребности (вместимость +
 * дефицит). Дефицит ≤ 0 — профицит, потребность закрыта на 100%.
 */
const provisionPct = (
  capacity: number | null,
  deficit: number | null,
): number | null => {
  if (capacity === null || deficit === null) return null;
  if (deficit <= 0) return 100;
  return (capacity / (capacity + deficit)) * 100;
};

/** Значение в км / ед. / чел. — «—», если показателя нет. */
const kmOf = (v: number | null, unit: string): string | null =>
  v !== null ? `${fmt1(v)} ${unit}` : null;

interface Widget {
  key: string;
  /** Оценка высоты в строках — вход балансировщика колонок. */
  weight: number;
  node: ReactNode;
}

/** Карточка виджета + шапка таблицы: примерно три строки высоты. */
const CHROME = 3;

/**
 * Раскладка виджетов по двум колонкам: самые высокие распределяются
 * первыми, каждый — в колонку, которая пока короче (LPT). Внутри колонки
 * возвращается объявленный порядок, поэтому раскладка предсказуема, а низ
 * колонок сходится — под виджетами не остаётся пустого места при любом
 * наборе видимых блоков.
 */
const balanceColumns = (items: Widget[]): [Widget[], Widget[]] => {
  const heights: [number, number] = [0, 0];
  const column = new Map<string, 0 | 1>();
  [...items]
    .sort((a, b) => b.weight - a.weight)
    .forEach((item) => {
      const target = heights[1] < heights[0] ? 1 : 0;
      column.set(item.key, target);
      heights[target] += item.weight;
    });
  return [
    items.filter((item) => column.get(item.key) === 0),
    items.filter((item) => column.get(item.key) === 1),
  ];
};

/** «Водоснабжение» — население по источникам (№14 витрины). */
const WaterCard = ({
  water,
  population,
  totalVillages,
}: {
  water: SnpWater | null;
  population: number | null;
  totalVillages: number | null;
}) => {
  const { t } = useTranslation();
  const countLabel = useCountLabel();
  const COLS = 3;
  return (
    <WidgetCard
      testId="snp-water"
      title={t('analytics.snpNeeds.engineering.water.title')}
    >
      {water === null ? (
        <NoData text={t('common.noData')} />
      ) : (
        <Table>
          <TableHeader>
            <TableRow className={ROW}>
              <HeadCell>{t('analytics.snpNeeds.columns.source')}</HeadCell>
              <HeadCell num>{t('analytics.snpNeeds.columns.share')}</HeadCell>
              <HeadCell num>
                {t('analytics.snpNeeds.columns.population')}
              </HeadCell>
            </TableRow>
          </TableHeader>
          <TableBody>
            {/* «Всего» — население территории: сумма по источникам. */}
            <TableRow className={ROW}>
              <TextCell className="font-bold">
                {t('analytics.snpNeeds.engineering.water.totalRow')}
              </TextCell>
              <ValueCell
                tone="share"
                value={population !== null ? '100%' : null}
              />
              <ValueCell value={population !== null ? fmt(population) : null} />
            </TableRow>
            {water.sources.map((source) => (
              <TableRow
                key={source.key}
                className={ROW}
                data-testid={`snp-water-${source.key}`}
              >
                <TextCell>
                  {t(
                    `analytics.snpNeeds.engineering.water.sources.${source.key}`,
                    { defaultValue: source.key },
                  )}
                </TextCell>
                <ShareCell pct={source.pct} />
                <ValueCell
                  tone={
                    source.key === 'central'
                      ? 'good'
                      : source.key === 'delivered'
                        ? 'danger'
                        : 'plain'
                  }
                  value={source.people !== null ? fmt(source.people) : null}
                />
              </TableRow>
            ))}
            <FootRow
              cols={COLS}
              label={t(
                'analytics.snpNeeds.engineering.water.deliveredVillages',
              )}
              values={[
                {
                  value: countLabel(water.delivered, totalVillages),
                  tone: (water.delivered?.present ?? 0) > 0 ? 'danger' : 'good',
                },
              ]}
            />
            <FootRow
              cols={COLS}
              label={t('analytics.snpNeeds.engineering.water.badQuality')}
              values={[
                {
                  value: countLabel(water.badQuality, totalVillages),
                  tone:
                    (water.badQuality?.present ?? 0) > 0 ? 'danger' : 'good',
                },
              ]}
            />
          </TableBody>
        </Table>
      )}
    </WidgetCard>
  );
};

/** «Электроснабжение» — сёла по износу ЛЭП и подстанций. */
const ElectricityCard = ({
  electricity,
  totalVillages,
}: {
  electricity: SnpElectricity | null;
  totalVillages: number | null;
}) => {
  const { t } = useTranslation();
  const countLabel = useCountLabel();
  const COLS = 4;
  /** Бакеты подстанций — по ключу износа ЛЭП (порядок один и тот же). */
  const tpOf = (key: string): string | null => {
    const villages =
      electricity?.tpWearDistribution.find((row) => row.key === key)
        ?.villages ?? null;
    return villages !== null ? fmt(villages) : null;
  };
  return (
    <WidgetCard
      testId="snp-electricity"
      title={t('analytics.snpNeeds.engineering.electricity.title')}
    >
      {electricity === null ? (
        <NoData text={t('common.noData')} />
      ) : (
        <Table>
          <TableHeader>
            <TableRow className={ROW}>
              <HeadCell>{t('analytics.snpNeeds.columns.wear')}</HeadCell>
              <HeadCell num>{t('analytics.snpNeeds.columns.share')}</HeadCell>
              <HeadCell num>{t('analytics.snpNeeds.columns.lines')}</HeadCell>
              <HeadCell num>
                {t('analytics.snpNeeds.columns.substations')}
              </HeadCell>
            </TableRow>
          </TableHeader>
          <TableBody>
            {electricity.wearDistribution.map((row) => (
              <TableRow
                key={row.key}
                className={ROW}
                data-testid={`snp-wear-${row.key}`}
              >
                <TextCell>
                  {t(
                    `analytics.snpNeeds.engineering.electricity.${row.key}`,
                    { defaultValue: row.key },
                  )}
                </TextCell>
                <ShareCell pct={row.pct} />
                <ValueCell
                  tone={
                    row.key === 'over75'
                      ? 'danger'
                      : row.key === 'under50'
                        ? 'good'
                        : 'plain'
                  }
                  value={row.villages !== null ? fmt(row.villages) : null}
                />
                <ValueCell
                  tone={
                    row.key === 'over75'
                      ? 'danger'
                      : row.key === 'under50'
                        ? 'good'
                        : 'plain'
                  }
                  value={tpOf(row.key)}
                />
              </TableRow>
            ))}
            <FootRow
              cols={COLS}
              label={t(
                'analytics.snpNeeds.engineering.electricity.linesSubstations',
              )}
              values={[
                {
                  value: kmOf(
                    electricity.linesKm,
                    t('analytics.snpNeeds.units.km'),
                  ),
                  tone: 'muted',
                },
                {
                  value:
                    electricity.substations !== null
                      ? `${fmt(electricity.substations)} ${t('analytics.snpNeeds.units.units')}`
                      : null,
                },
              ]}
            />
            <FootRow
              cols={COLS}
              label={t('analytics.snpNeeds.engineering.electricity.avgWear')}
              values={[
                {
                  value:
                    electricity.avgWearPct !== null
                      ? `${fmt1(electricity.avgWearPct)}%`
                      : null,
                  tone: 'danger',
                },
              ]}
            />
            <FootRow
              cols={COLS}
              label={t('analytics.snpNeeds.engineering.electricity.centralized')}
              values={[
                {
                  value: countLabel(electricity.centralized, totalVillages),
                  tone: 'good',
                },
              ]}
            />
          </TableBody>
        </Table>
      )}
    </WidgetCard>
  );
};

/** «Связь и интернет» — сёла без охвата (обратные пары). */
const CommsCard = ({
  comms,
  totalVillages,
}: {
  comms: SnpComms | null;
  totalVillages: number | null;
}) => {
  const { t } = useTranslation();
  const rows: Array<{ key: string; count: SnpCount | null; good: boolean }> = [
    { key: 'noMobile', count: without(comms?.mobile), good: false },
    { key: 'noInternet', count: without(comms?.mobileInternet), good: false },
    { key: 'noBroadband', count: without(comms?.broadband), good: false },
    { key: 'publicBroadband', count: comms?.publicBroadband ?? null, good: true },
  ];
  return (
    <WidgetCard
      testId="snp-comms"
      title={t('analytics.snpNeeds.engineering.comms.title')}
    >
      {comms === null ? (
        <NoData text={t('common.noData')} />
      ) : (
        <Table>
          <TableHeader>
            <TableRow className={ROW}>
              <HeadCell>{t('analytics.snpNeeds.columns.indicator')}</HeadCell>
              <HeadCell num>{t('analytics.snpNeeds.columns.share')}</HeadCell>
              <HeadCell num>
                {t('analytics.snpNeeds.columns.villagesCount')}
              </HeadCell>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((row) => (
              <TableRow
                key={row.key}
                className={ROW}
                data-testid={`snp-comms-${row.key}`}
              >
                <TextCell>
                  {t(`analytics.snpNeeds.engineering.comms.${row.key}`)}
                </TextCell>
                <ShareCell
                  pct={sharePct(
                    row.count?.present ?? null,
                    row.count?.answered ?? totalVillages,
                  )}
                />
                <CountCell
                  count={row.count}
                  base={totalVillages}
                  tone={
                    row.good
                      ? 'good'
                      : (row.count?.present ?? 0) > 0
                        ? 'danger'
                        : 'plain'
                  }
                />
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </WidgetCard>
  );
};

/** «Газоснабжение» — охват сёл; на пилоте графа обычно пустая. */
const GasCard = ({
  gas,
  totalVillages,
}: {
  gas: SnpGas | null;
  totalVillages: number | null;
}) => {
  const { t } = useTranslation();
  const COLS = 3;
  const rows: Array<{ key: string; count: SnpCount | null }> = [
    { key: 'natural', count: gas?.natural ?? null },
    { key: 'lpg', count: gas?.lpg ?? null },
  ];
  // На пилоте графа газификации обычно пустая — тогда виджет остаётся с
  // заголовком и подписью «нет данных» (как на прототипе).
  const empty =
    gas === null ||
    (rows.every((row) => row.count?.present == null) &&
      gas.housesPct === null &&
      gas.gasifiedPopulation === null);
  return (
    <WidgetCard
      testId="snp-gas"
      title={t('analytics.snpNeeds.engineering.gas.title')}
    >
      {empty || gas === null ? (
        <NoData text={t('common.noData')} />
      ) : (
        <Table>
          <TableHeader>
            <TableRow className={ROW}>
              <HeadCell>{t('analytics.snpNeeds.columns.indicator')}</HeadCell>
              <HeadCell num>{t('analytics.snpNeeds.columns.share')}</HeadCell>
              <HeadCell num>
                {t('analytics.snpNeeds.columns.villagesCount')}
              </HeadCell>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((row) => (
              <TableRow
                key={row.key}
                className={ROW}
                data-testid={`snp-gas-${row.key}`}
              >
                <TextCell>
                  {t(`analytics.snpNeeds.engineering.gas.${row.key}`)}
                </TextCell>
                <ShareCell
                  pct={sharePct(
                    row.count?.present ?? null,
                    row.count?.answered ?? totalVillages,
                  )}
                />
                <CountCell count={row.count} base={totalVillages} />
              </TableRow>
            ))}
            <FootRow
              cols={COLS}
              label={t('analytics.snpNeeds.engineering.gas.housesShare')}
              values={[
                {
                  value:
                    gas.housesPct !== null ? `${fmt1(gas.housesPct)}%` : null,
                  tone: 'share',
                },
              ]}
            />
            <FootRow
              cols={COLS}
              label={t('analytics.snpNeeds.engineering.gas.population')}
              values={[
                {
                  value:
                    gas.gasifiedPopulation !== null
                      ? `${fmt(gas.gasifiedPopulation)} ${t('analytics.snpNeeds.deviations.peopleShort')}`
                      : null,
                },
              ]}
            />
          </TableBody>
        </Table>
      )}
    </WidgetCard>
  );
};

/** «Внутрипоселковые дороги» — состояние, покрытие, связанность. */
const RoadsCard = ({
  roads,
  totalVillages,
}: {
  roads: SnpNeedsData['roads'];
  totalVillages: number | null;
}) => {
  const { t } = useTranslation();
  const countLabel = useCountLabel();
  const COLS = 4;
  const surface = roads?.surface ?? null;
  const transport = roads?.transport ?? null;
  return (
    <WidgetCard
      testId="snp-roads"
      title={t('analytics.snpNeeds.roads.innerTitle')}
    >
      {roads === null ? (
        <NoData text={t('common.noData')} />
      ) : (
        <Table>
          <TableHeader>
            <TableRow className={ROW}>
              <HeadCell>{t('analytics.snpNeeds.columns.condition')}</HeadCell>
              <HeadCell />
              <HeadCell num>{t('analytics.snpNeeds.columns.share')}</HeadCell>
              <HeadCell num>{t('analytics.snpNeeds.columns.length')}</HeadCell>
            </TableRow>
          </TableHeader>
          <TableBody>
            {roads.condition.map((row) => (
              <TableRow
                key={row.key}
                className={ROW}
                data-testid={`snp-road-${row.key}`}
              >
                <TextCell>
                  {t(`analytics.snpNeeds.roads.condition.${row.key}`, {
                    defaultValue: row.key,
                  })}
                </TextCell>
                <BarCell pct={row.pct} />
                <ShareCell pct={row.pct} />
                <ValueCell
                  tone={
                    row.key === 'bad'
                      ? 'danger'
                      : row.key === 'good'
                        ? 'good'
                        : 'plain'
                  }
                  value={row.km !== null ? fmt1(row.km) : null}
                />
              </TableRow>
            ))}
            <FootRow
              cols={COLS}
              label={t('analytics.snpNeeds.roads.totalRow')}
              values={[
                { value: roads.totalKm !== null ? fmt1(roads.totalKm) : null },
              ]}
            />
            <FootRow
              cols={COLS}
              label={t('analytics.snpNeeds.roads.surface.repairRow')}
              values={[
                {
                  value:
                    surface?.capitalRepairKm != null
                      ? fmt1(surface.capitalRepairKm)
                      : null,
                  tone: 'danger',
                },
                {
                  value:
                    surface?.mediumRepairKm != null
                      ? fmt1(surface.mediumRepairKm)
                      : null,
                },
              ]}
            />
            <FootRow
              cols={COLS}
              label={t('analytics.snpNeeds.roads.surface.surfaceRow')}
              values={[
                {
                  value: surface?.hardKm != null ? fmt1(surface.hardKm) : null,
                  tone: 'good',
                },
                {
                  value: surface?.noneKm != null ? fmt1(surface.noneKm) : null,
                },
              ]}
            />
            <FootRow
              cols={COLS}
              label={t('analytics.snpNeeds.roads.transport.offroad')}
              values={[
                {
                  value:
                    transport?.offroadKm != null &&
                    transport.offroadVillages != null
                      ? t('analytics.snpNeeds.roads.transport.offroadValue', {
                          km: fmt1(transport.offroadKm),
                          n: transport.offroadVillages,
                        })
                      : null,
                  tone: 'danger',
                },
              ]}
            />
            <FootRow
              cols={COLS}
              label={t('analytics.snpNeeds.roads.transport.routesTotal')}
              values={[
                {
                  value:
                    transport?.routesTotalKm != null
                      ? fmt1(transport.routesTotalKm)
                      : null,
                },
              ]}
            />
            <FootRow
              cols={COLS}
              testId="snp-transport"
              label={t('analytics.snpNeeds.roads.transport.noBusVillages')}
              values={[
                {
                  value: countLabel(without(transport?.bus), totalVillages),
                  tone:
                    (without(transport?.bus)?.present ?? 0) > 0
                      ? 'danger'
                      : 'good',
                },
              ]}
            />
          </TableBody>
        </Table>
      )}
    </WidgetCard>
  );
};

/** «Образование» — обеспеченность местами (№15 витрины). */
const EducationCard = ({
  education,
  totalVillages,
}: {
  education: SnpEducation | null;
  totalVillages: number | null;
}) => {
  const { t } = useTranslation();
  const countLabel = useCountLabel();
  const COLS = 4;
  const rows = [
    {
      key: 'kindergartens',
      capacity: education?.kindergartenCapacity ?? null,
      deficit: education?.kindergartenDeficit ?? null,
    },
    {
      key: 'schools',
      capacity: education?.schoolCapacity ?? null,
      deficit: education?.schoolDeficit ?? null,
    },
  ];
  return (
    <WidgetCard
      testId="snp-education"
      title={t('analytics.snpNeeds.education.title')}
    >
      {education === null ? (
        <NoData text={t('common.noData')} />
      ) : (
        <Table>
          <TableHeader>
            <TableRow className={ROW}>
              <HeadCell>{t('analytics.snpNeeds.columns.indicator')}</HeadCell>
              <HeadCell num>
                {t('analytics.snpNeeds.columns.provision')}
              </HeadCell>
              <HeadCell num>{t('analytics.snpNeeds.columns.capacity')}</HeadCell>
              <HeadCell num>{t('analytics.snpNeeds.columns.deficit')}</HeadCell>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((row) => {
              const provision = provisionPct(row.capacity, row.deficit);
              return (
                <TableRow
                  key={row.key}
                  className={ROW}
                  data-testid={`snp-education-${row.key}`}
                >
                  <TextCell>
                    {t(`analytics.snpNeeds.education.${row.key}`)}
                  </TextCell>
                  <ValueCell
                    tone="share"
                    value={provision !== null ? `${fmt1(provision)}%` : null}
                  />
                  <ValueCell
                    value={row.capacity !== null ? fmt(row.capacity) : null}
                  />
                  {/* Дефицит числом и красным, включая отрицательные
                      значения (замечание ревью); «потребность закрыта» —
                      только при ровно нуле. */}
                  <ValueCell
                    tone={row.deficit === 0 ? 'good' : 'danger'}
                    value={
                      row.deficit === null
                        ? null
                        : row.deficit === 0
                          ? t('analytics.snpNeeds.education.covered')
                          : fmt(row.deficit)
                    }
                  />
                </TableRow>
              );
            })}
            <FootRow
              cols={COLS}
              label={t('analytics.snpNeeds.education.schoolDistance')}
              values={[
                {
                  value: kmOf(
                    education.schoolDistanceKm,
                    t('analytics.snpNeeds.units.km'),
                  ),
                },
              ]}
            />
            {/* Подвоз: API — парой «N из M ответивших» (school_busing),
                демо-мок — как да/нет. */}
            <FootRow
              cols={COLS}
              testId="snp-education-busing"
              label={t('analytics.snpNeeds.education.busing')}
              values={[
                {
                  value:
                    education.busing !== null
                      ? countLabel(education.busing, totalVillages)
                      : education.busingOrganized === null
                        ? null
                        : t(
                            education.busingOrganized
                              ? 'analytics.snpNeeds.yes'
                              : 'analytics.snpNeeds.no',
                          ),
                  tone: education.busingOrganized === false ? 'danger' : 'plain',
                },
              ]}
            />
          </TableBody>
        </Table>
      )}
    </WidgetCard>
  );
};

/** «Здравоохранение — покрытие сёл» (№16 витрины). */
const FacilitiesCard = ({
  healthcare,
  totalVillages,
}: {
  healthcare: SnpHealthcare | null;
  totalVillages: number | null;
}) => {
  const { t } = useTranslation();
  const countLabel = useCountLabel();
  const COLS = 4;
  const rows = [...(healthcare?.facilities ?? [])].sort(
    (a, b) => (b.count.present ?? 0) - (a.count.present ?? 0),
  );
  return (
    <WidgetCard
      testId="snp-healthcare-facilities"
      title={t('analytics.snpNeeds.healthcare.coverageTitle')}
    >
      {healthcare === null ? (
        <NoData text={t('common.noData')} />
      ) : (
        <Table>
          <TableHeader>
            <TableRow className={ROW}>
              <HeadCell>{t('analytics.snpNeeds.columns.facility')}</HeadCell>
              <HeadCell />
              <HeadCell num>{t('analytics.snpNeeds.columns.share')}</HeadCell>
              <HeadCell num>
                {t('analytics.snpNeeds.columns.villagesCount')}
              </HeadCell>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((row) => {
              // Знаменатель — ответившие по этому типу учреждения
              // (медпункт заполнили не все сёла).
              const pct = sharePct(
                row.count.present,
                row.count.answered ?? totalVillages,
              );
              return (
                <TableRow
                  key={row.key}
                  className={ROW}
                  data-testid={`snp-facility-${row.key}`}
                >
                  <TextCell>
                    {t(`analytics.snpNeeds.healthcare.facilities.${row.key}`, {
                      defaultValue: row.key,
                    })}
                  </TextCell>
                  <BarCell pct={pct} />
                  <ShareCell pct={pct} />
                  <CountCell
                    count={row.count}
                    base={totalVillages}
                    tone={(row.count.present ?? 0) > 0 ? 'good' : 'danger'}
                  />
                </TableRow>
              );
            })}
            <FootRow
              cols={COLS}
              label={t('analytics.snpNeeds.healthcare.totalFacilitiesBeds')}
              values={[
                {
                  value:
                    healthcare.totalFacilities !== null
                      ? `${fmt(healthcare.totalFacilities)} ${t('analytics.snpNeeds.units.units')}`
                      : null,
                  tone: 'muted',
                },
                {
                  value:
                    healthcare.beds !== null ? fmt(healthcare.beds) : null,
                },
              ]}
            />
            <FootRow
              cols={COLS}
              label={t('analytics.snpNeeds.healthcare.noFacility')}
              values={[
                {
                  value: countLabel(
                    healthcare.villagesNoFacility !== null
                      ? {
                          present: healthcare.villagesNoFacility,
                          answered: totalVillages,
                        }
                      : null,
                    totalVillages,
                  ),
                  tone:
                    (healthcare.villagesNoFacility ?? 0) > 0
                      ? 'danger'
                      : 'good',
                },
              ]}
            />
          </TableBody>
        </Table>
      )}
    </WidgetCard>
  );
};

/** «Медицинские кадры» — штат / факт по территории (FR-5.4). */
const StaffCard = ({
  healthcare,
  themes,
  totalVillages,
}: {
  healthcare: SnpHealthcare | null;
  themes: SnpTheme[];
  totalVillages: number | null;
}) => {
  const { t } = useTranslation();
  const countLabel = useCountLabel();
  const COLS = 4;
  const staff = healthcare?.staff ?? null;
  const rows = [
    {
      key: 'doctors',
      actual: staff?.doctorsActual ?? null,
      staffed: staff?.doctorsStaffed ?? null,
      deficitPct: staff?.doctorsDeficitPct ?? null,
    },
    {
      key: 'nurses',
      actual: staff?.nursesActual ?? null,
      staffed: staff?.nursesStaffed ?? null,
      deficitPct: staff?.nursesDeficitPct ?? null,
    },
  ];
  return (
    <WidgetCard
      testId="snp-healthcare-staff"
      title={t('analytics.snpNeeds.healthcare.staffCardTitle')}
    >
      {staff === null ? (
        <NoData text={t('common.noData')} />
      ) : (
        <Table>
          <TableHeader>
            <TableRow className={ROW}>
              <HeadCell>{t('analytics.snpNeeds.columns.category')}</HeadCell>
              <HeadCell num>{t('analytics.snpNeeds.columns.staffed')}</HeadCell>
              <HeadCell num>{t('analytics.snpNeeds.columns.byStaff')}</HeadCell>
              <HeadCell num>{t('analytics.snpNeeds.columns.actual')}</HeadCell>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((row) => {
              // Процент дефицита считает бэк (doctors_deficit_pct);
              // укомплектованность — дополнение к нему.
              const deficitPct =
                row.deficitPct ??
                (row.actual !== null && row.staffed !== null && row.staffed > 0
                  ? ((row.staffed - row.actual) / row.staffed) * 100
                  : null);
              return (
                <TableRow
                  key={row.key}
                  className={ROW}
                  data-testid={`snp-staff-${row.key}`}
                >
                  <TextCell>
                    {t(`analytics.snpNeeds.healthcare.${row.key}`)}
                  </TextCell>
                  <ValueCell
                    tone="share"
                    value={
                      deficitPct !== null ? `${fmt1(100 - deficitPct)}%` : null
                    }
                  />
                  <ValueCell
                    value={row.staffed !== null ? fmt1(row.staffed) : null}
                  />
                  <ValueCell
                    tone={(deficitPct ?? 0) > 0 ? 'danger' : 'good'}
                    value={row.actual !== null ? fmt1(row.actual) : null}
                  />
                </TableRow>
              );
            })}
            {/* Сёла с дефицитом кадров — из тем отклонений (у показателя
                свой знаменатель). */}
            {(
              [
                ['doctorsDeficit', 'villagesDoctorDeficit'],
                ['nursesDeficit', 'villagesNurseDeficit'],
              ] as const
            ).map(([themeKey, labelKey]) => {
              const theme = themes.find((row) => row.key === themeKey);
              return (
                <FootRow
                  key={themeKey}
                  cols={COLS}
                  label={t(`analytics.snpNeeds.healthcare.${labelKey}`)}
                  values={[
                    {
                      value: countLabel(
                        theme
                          ? {
                              present: theme.villages,
                              answered: theme.answered,
                            }
                          : null,
                        totalVillages,
                      ),
                      tone: (theme?.villages ?? 0) > 0 ? 'danger' : 'good',
                    },
                  ]}
                />
              );
            })}
            <FootRow
              cols={COLS}
              label={t('analytics.snpNeeds.healthcare.pharmacies')}
              values={[
                {
                  value:
                    healthcare?.pharmacies != null
                      ? `${fmt(healthcare.pharmacies)} ${t('analytics.snpNeeds.units.units')}`
                      : null,
                },
              ]}
            />
          </TableBody>
        </Table>
      )}
    </WidgetCard>
  );
};

interface SnpNeedsSectionProps {
  /** Режим витрины «Все» (US-04.1): только ключевые виджеты FR-2.2. */
  summary?: boolean;
}

export const SnpNeedsSection = ({
  summary = false,
}: SnpNeedsSectionProps = {}) => {
  const { t } = useTranslation();
  const { data, loading, isHardKato } = useAnalyticsSection<SnpNeedsData>(
    'snpNeeds',
    getAnalyticsSnpNeeds,
  );

  const showPlashka = useModulePresent('analytics.snp-needs-plashka');
  const showDeviations = useModulePresent('analytics.snp-needs-villages');
  const showEngineering = useModulePresent('analytics.snp-needs-engineering');
  const showRoads = useModulePresent('analytics.snp-needs-roads');
  const showEducation = useModulePresent('analytics.snp-needs-education');
  const showHealthcare = useModulePresent('analytics.snp-needs-healthcare');

  // FR-6.2: смена территории (и года) перезапрашивает все блоки.

  const d: SnpNeedsData = isHardKato
    ? (analyticsSnpNeeds as SnpNeedsData)
    : (data ?? EMPTY);

  const { water, electricity, gas, comms } = d.engineering;
  const total = d.totalVillages;

  // Счётчики градаций: готовое распределение из ответа, иначе — из списка
  // сёл (пороги — snpGrades).
  const gradeCounts: Record<string, number> =
    d.distribution !== null
      ? {
          none: d.distribution.none ?? 0,
          low: d.distribution.low ?? 0,
          mid: d.distribution.mid ?? 0,
          high: d.distribution.high ?? 0,
        }
      : d.villages.reduce<Record<string, number>>((acc, village) => {
          const grade = gradeOf(
            village.deviationCount ?? village.deviations.length,
          );
          acc[grade] = (acc[grade] ?? 0) + 1;
          return acc;
        }, {});
  const gradesTotal = Object.values(gradeCounts).reduce(
    (acc, value) => acc + value,
    0,
  );

  /** Виджеты в порядке значимости — раскладку по колонкам считает балансировщик. */
  const widgets: Widget[] = [];

  if (!summary && showDeviations) {
    widgets.push({
      key: 'villages',
      weight:
        (d.villages.length > 0 ? Math.min(d.villages.length, 8) + 1 : 1) +
        CHROME,
      node: <SnpVillages villages={d.villages} />,
    });
  }
  if (showEngineering) {
    widgets.push({
      key: 'water',
      weight: (water !== null ? water.sources.length + 3 : 1) + CHROME,
      node: (
        <WaterCard water={water} population={d.population} totalVillages={total} />
      ),
    });
  }
  if (!summary && showEngineering) {
    widgets.push({
      key: 'electricity',
      weight:
        (electricity !== null ? electricity.wearDistribution.length + 3 : 1) +
        CHROME,
      node: <ElectricityCard electricity={electricity} totalVillages={total} />,
    });
  }
  if (!summary && showRoads) {
    widgets.push({
      key: 'roads',
      weight: (d.roads !== null ? d.roads.condition.length + 5 : 1) + CHROME,
      node: <RoadsCard roads={d.roads} totalVillages={total} />,
    });
  }
  if (!summary && showEngineering) {
    widgets.push({
      key: 'comms',
      weight: (comms !== null ? 4 : 1) + CHROME,
      node: <CommsCard comms={comms} totalVillages={total} />,
    });
    widgets.push({
      key: 'gas',
      weight: (gas !== null ? 4 : 1) + CHROME,
      node: <GasCard gas={gas} totalVillages={total} />,
    });
  }
  if (showEducation) {
    widgets.push({
      key: 'education',
      weight: (d.education !== null ? 4 : 1) + CHROME,
      node: <EducationCard education={d.education} totalVillages={total} />,
    });
  }
  if (showHealthcare) {
    widgets.push({
      key: 'facilities',
      weight:
        (d.healthcare !== null ? d.healthcare.facilities.length + 2 : 1) +
        CHROME,
      node: <FacilitiesCard healthcare={d.healthcare} totalVillages={total} />,
    });
  }
  if (!summary && showHealthcare) {
    widgets.push({
      key: 'staff',
      weight: (d.healthcare?.staff != null ? 5 : 1) + CHROME,
      node: (
        <StaffCard
          healthcare={d.healthcare}
          themes={d.themes}
          totalVillages={total}
        />
      ),
    });
  }

  const [leftColumn, rightColumn] = balanceColumns(widgets);

  return (
    <section className="space-y-4" data-testid="analytics-snp-needs">
      <div className="flex items-center gap-4">
        <h2 className="shrink-0 text-xl font-extrabold">
          {t('analytics.snpNeeds.title')}
        </h2>
        <div className="h-px flex-1 bg-surface-border" aria-hidden="true" />
        <ClipboardList
          className="size-6 shrink-0 text-brand-600"
          strokeWidth={1.8}
          aria-hidden="true"
        />
      </div>

      {loading ? (
        <BlockLoader />
      ) : (
        <>
          {/* Ряд стат-карточек: СНП с населением + градации отклонений. */}
          {!summary && showPlashka && (
            <div
              className="grid gap-3.5 sm:grid-cols-2 xl:grid-cols-5"
              data-testid="snp-plashka"
            >
              <StatCard
                testId="snp-card-total"
                title={t('analytics.snpNeeds.cards.total')}
                value={total !== null ? fmt(total) : null}
                unit={t('analytics.snpNeeds.plashka.snp')}
                footnotes={[
                  ...(d.population !== null
                    ? [
                        {
                          text: t('analytics.snpNeeds.cards.people', {
                            value: fmt(d.population),
                          }),
                          bold: true,
                        },
                      ]
                    : []),
                  // Источник и год данных «Мониторинга СНП» — единственное
                  // место, где виден год выгрузки.
                  {
                    text: [
                      t('analytics.snpNeeds.plashka.caption'),
                      d.year !== null ? String(d.year) : null,
                    ]
                      .filter(Boolean)
                      .join(' · '),
                  },
                ]}
              />
              {SNP_GRADES.map((grade) => {
                const count = gradeCounts[grade] ?? 0;
                const pct = sharePct(count, gradesTotal || total);
                return (
                  <StatCard
                    key={grade}
                    testId={`snp-grade-${grade}`}
                    title={t(`analytics.snpNeeds.plashka.grades.${grade}`)}
                    value={total !== null ? fmt(count) : null}
                    unit={t('analytics.snpNeeds.plashka.villagesUnit')}
                    footnotes={
                      pct !== null
                        ? [
                            {
                              text: t('analytics.snpNeeds.cards.ofTotal', {
                                pct: fmt1(pct),
                              }),
                            },
                          ]
                        : []
                    }
                  />
                );
              })}
            </div>
          )}

          {/* Темы отклонений — на всю ширину: бары читаются только широко. */}
          {!summary && showDeviations && (
            <SnpThemes themes={d.themes} totalVillages={total} />
          )}

          {/* Остальные виджеты — две колонки, распределённые по высоте. */}
          <div className="grid items-start gap-3.5 xl:grid-cols-2">
            <div className="space-y-3.5">
              {leftColumn.map((widget) => (
                <div key={widget.key}>{widget.node}</div>
              ))}
            </div>
            <div className="space-y-3.5">
              {rightColumn.map((widget) => (
                <div key={widget.key}>{widget.node}</div>
              ))}
            </div>
          </div>
        </>
      )}
    </section>
  );
};
